import { useEffect, useState, useCallback } from 'react';
import {
    Box, Grid, Card, CardContent, Typography, Chip, IconButton,
    TextField, Select, MenuItem, FormControl, InputLabel,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Drawer, Divider, Button, CircularProgress, Tooltip,
    LinearProgress, InputAdornment, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Stack,
    Tabs, Tab, Skeleton, Alert, useTheme, useMediaQuery,
    FormLabel,
    OutlinedInput,
    Snackbar,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

import {
    Users, UserPlus, CalendarDays, DollarSign, Search,
    ChevronRight, Phone, Mail, MapPin, Building2, CreditCard,
    CheckCircle2, Clock, XCircle, AlertTriangle, Plane, Heart,
    Baby, Coffee, Star, FileText, Banknote, Shield, ArrowRight,
    RefreshCw, Wallet, TrendingUp, Inbox,
    UserCheck,
    UserMinus,
    Smartphone,
    Copy,
} from 'lucide-react';
import apiClient from '../../../services/api-client';
import { KpiCard } from '../../../components/charts/kpi-card';

// ── Palette & theme tokens ──────────────────────────────────────────────────
const C = {
    bg: '#faf7f2',
    surface: '#ffffff',
    navy: '#1a1a2e',
    navyLight: '#2d2d4a',
    gold: '#c9a84c',
    goldDark: '#b08d3a',
    border: 'rgba(201,168,76,0.12)',
    borderLight: 'rgba(201,168,76,0.08)',
    borderFocus: 'rgba(201,168,76,0.3)',
    muted: '#888',
    faint: '#999',
    dimmer: '#aaa',
    pale: '#f0ebe0',
    white60: 'rgba(255,255,255,0.6)',
    error: '#c62828',
    errorBg: '#fef2f2',
    success: '#2e7d32',
};

const DEPT_COLORS = ['#c9a84c', '#1a1a2e', '#2e7d32', '#ef6c00', '#6a1b9a', '#1565c0', '#78909c'];

const roleConfig = {
    // --- Postes avec accès plateforme ---
    CHEF_AGENCE: { label: "Chef d'Agence", color: '#c9a84c', bg: 'rgba(201,168,76,0.08)' },
    AGENT_COMPTOIR: { label: 'Agent de Comptoir', color: '#1565c0', bg: 'rgba(21,101,192,0.06)' },
    BILLETISTE: { label: 'Billettiste', color: '#00838f', bg: 'rgba(0,131,143,0.06)' },
    COMPTABLE: { label: 'Comptable', color: '#2e7d32', bg: 'rgba(46,125,50,0.06)' },

    // --- Postes opérationnels (sans accès plateforme) ---
    CHAUFFEUR: { label: 'Chauffeur / Livreur', color: '#ef6c00', bg: 'rgba(239,108,0,0.06)' },
    COURSIER: { label: 'Coursier', color: '#78909c', bg: 'rgba(120,144,156,0.06)' },
    AGENT_ENTRETIEN: { label: "Agent d'Entretien", color: '#6a1b9a', bg: 'rgba(106,27,154,0.06)' },
    GARDIEN: { label: 'Agent de Sécurité', color: '#d84315', bg: 'rgba(216,67,21,0.06)' },

    // --- Administration ---
    ADMINISTRATEUR: { label: 'Administrateur plateforme', color: '#4527a0', bg: 'rgba(69,39,160,0.06)' },
};

const contractConfig = {
    CDI: { label: 'CDI', color: '#2e7d32' },
    CDD: { label: 'CDD', color: '#ef6c00' },
    STAGE: { label: 'Stage', color: '#1565c0' },
    FREELANCE: { label: 'Freelance', color: '#6a1b9a' },
};

const statusConfig = {
    true: { label: 'Actif', color: '#2e7d32' },
    'EN_CONGÉ': { label: 'En congé', color: '#1565c0' },
    'EN_ARRÊT_MALADIE': { label: 'Arrêt maladie', color: '#ef6c00' },
    false: { label: 'Suspendu', color: '#c62828' },
    'DÉPART': { label: 'Départ', color: '#78909c' },
};

const leaveTypeConfig = {
    CONGÉ_ANNUEL: { label: 'Congé annuel', icon: <Plane size={16} />, color: '#1565c0' },
    CONGÉ_MALADIE: { label: 'Congé maladie', icon: <Heart size={16} />, color: '#c62828' },
    CONGÉ_MATERNITÉ: { label: 'Congé maternité', icon: <Baby size={16} />, color: '#d81b60' },
    CONGÉ_SANS_SOLDE: { label: 'Sans solde', icon: <AlertTriangle size={16} />, color: '#ef6c00' },
    RTT: { label: 'RTT', icon: <Coffee size={16} />, color: '#2e7d32' },
    CONGÉ_EXCEPTIONNEL: { label: 'Congé exceptionnel', icon: <Star size={16} />, color: '#6a1b9a' },
};

const leaveStatusConfig = {
    EN_ATTENTE: { label: 'En attente', icon: <Clock size={14} />, color: '#c9a84c' },
    'APPROUVÉ': { label: 'Approuvé', icon: <CheckCircle2 size={14} />, color: '#2e7d32' },
    'REFUSÉ': { label: 'Refusé', icon: <XCircle size={14} />, color: '#c62828' },
    'ANNULÉ': { label: 'Annulé', icon: <XCircle size={14} />, color: '#78909c' },
};

const salaryStatusConfig = {
    'PAYÉ': { label: 'Payé', color: '#2e7d32' },
    EN_ATTENTE: { label: 'En attente', color: '#c9a84c' },
    EN_COURS: { label: 'En cours', color: '#1565c0' },
};

const sections = [
    { id: 'employees', label: 'Personnel', icon: <Users size={16} /> },
    // { id: 'leaves', label: 'Congés', icon: <CalendarDays size={16} /> },
    // { id: 'salaries', label: 'Paie', icon: <DollarSign size={16} /> },
];

// ── Shared styles ────────────────────────────────────────────────────────────
const cardSx = {
    bgcolor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 0.5,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    transition: 'all 0.2s ease',
    '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' },
};

const inputSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: C.surface,
        borderRadius: 0.5,
        '& fieldset': { borderColor: 'rgba(201,168,76,0.2)' },
        '&:hover fieldset': { borderColor: C.gold },
        '&.Mui-focused fieldset': { borderColor: C.gold },
    },
};

const goldBtnSx = {
    background: `linear-gradient(135deg, ${C.goldDark}, ${C.gold})`,
    color: 'white',
    borderRadius: 0.5,
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(201,168,76,0.3)',
    '&:hover': {
        background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
        boxShadow: '0 4px 16px rgba(201,168,76,0.4)',
    },
};

// ── Helpers ──────────────────────────────────────────────────────────────────
function StatusChip({ label, color, size = 'small' }) {
    return (
        <Chip
            label={label}
            size={size}
            sx={{
                bgcolor: `${color}14`,
                color,
                fontWeight: 600,
                fontSize: 11,
                height: 24,
                letterSpacing: 0.3,
                borderRadius: 0.5,
            }}
        />
    );
}

function SectionDivider() {
    return <Divider sx={{ borderColor: C.borderLight, my: 2.5 }} />;
}

// ── Recharts custom tooltip ──────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <Paper sx={{
            px: 1.5, py: 1, bgcolor: C.navy, border: `1px solid rgba(201,168,76,0.2)`,
            borderRadius: 2, boxShadow: 6,
        }}>
            {label && (
                <Typography variant="caption" sx={{ color: C.gold, fontWeight: 600, display: 'block', mb: 0.5 }}>
                    {label}
                </Typography>
            )}
            {payload.map((item, i) => (
                <Typography key={i} variant="caption" sx={{ color: C.bg, display: 'block' }}>
                    {item.name}: <strong>{item.value}</strong>
                </Typography>
            ))}
        </Paper>
    );
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function PersonnelKPI({ title, value, subtitle, icon, color, bgColor }) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <Card sx={{ ...cardSx, border: `1px solid ${C.goldDark}` }}>
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0.5 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                        <Box sx={{
                            width: 44, height: 44, borderRadius: 0.5, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            bgcolor: bgColor, color,
                        }}>
                            {icon}
                        </Box>
                        <TrendingUp size={16} color={C.dimmer} style={{ opacity: 0.4 }} />
                    </Box>
                    <Typography sx={{ fontSize: 12, color: C.muted, mb: 0.5, fontWeight: 500 }}>{title}</Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 700, color: C.navy, lineHeight: 1.2, mb: 0.5 }}>
                        {value}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: C.dimmer }}>{subtitle}</Typography>
                </CardContent>
            </Card>
        </motion.div>
    );
}

// ── Skeleton Loader ──────────────────────────────────────────────────────────
function SkeletonLoader() {
    return (
        <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
                <Grid item xs={12} sm={6} lg={3} key={i}>
                    <Skeleton variant="rounded" height={130} sx={{ bgcolor: C.pale, borderRadius: 3 }} />
                </Grid>
            ))}
            <Grid item xs={12} xl={6}>
                <Skeleton variant="rounded" height={240} sx={{ bgcolor: C.pale, borderRadius: 3 }} />
            </Grid>
            <Grid item xs={12} xl={6}>
                <Skeleton variant="rounded" height={240} sx={{ bgcolor: C.pale, borderRadius: 3 }} />
            </Grid>
        </Grid>
    );
}

// ── Error State ──────────────────────────────────────────────────────────────
function ErrorState({ onRetry }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <Paper sx={{
                p: 4, textAlign: 'center', bgcolor: C.surface,
                border: `1px solid ${C.border}`, borderRadius: 3, maxWidth: 400,
            }}>
                <AlertTriangle size={40} color={C.error} style={{ margin: '0 auto 16px' }} />
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: C.navy, mb: 1 }}>
                    Impossible de charger les données
                </Typography>
                <Typography sx={{ fontSize: 13, color: C.muted, mb: 2 }}>
                    Une erreur est survenue lors de la récupération des informations du personnel.
                </Typography>
                <Button onClick={onRetry} sx={{ ...goldBtnSx, px: 3 }} startIcon={<RefreshCw size={14} />}>
                    Réessayer
                </Button>
            </Paper>
        </Box>
    );
}

// ── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ icon, title, subtitle }) {
    return (
        <Box py={8} textAlign="center">
            <Box sx={{ color: C.dimmer, mb: 1.5 }}>{icon}</Box>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.navy, mb: 0.5 }}>{title}</Typography>
            <Typography sx={{ fontSize: 13, color: C.dimmer }}>{subtitle}</Typography>
        </Box>
    );
}

// ── Employee Card ─────────────────────────────────────────────────────────────
function EmployeeCard({ employee, onSelect }) {
    const role = roleConfig[employee.poste] || { label: employee.poste, color: C.muted, bg: 'rgba(0,0,0,0.04)' };
    const contract = contractConfig[employee.typeContrat] || { label: employee.typeContrat, color: C.muted };
    const status = statusConfig[employee.actif] || { label: employee.actif, color: C.muted };
    const leavePercent = employee.totalLeaveDays > 0
        ? ((employee.totalLeaveDays - employee.remainingLeaveDays) / employee.totalLeaveDays) * 100
        : 0;

    const barColor = leavePercent > 80 ? '#c62828' : leavePercent > 50 ? '#ef6c00' : C.gold;

    return (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <Card
                sx={{
                    ...cardSx,
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0,
                        height: 3,
                        background: `linear-gradient(90deg, ${role.color}, ${role.color}88)`,
                        opacity: 0.8,
                    },
                }}
                onClick={() => onSelect(employee)}
            >
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                        {/* Avatar */}
                        <Box sx={{
                            width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
                            background: `linear-gradient(135deg, ${C.navy}, ${C.navyLight})`,
                            color: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 16, fontWeight: 700,
                            boxShadow: '0 2px 8px rgba(26,26,46,0.2)',
                        }}>
                            {employee.prenom?.[0]} {employee.nom?.[0]}
                        </Box>

                        <Box flex={1} minWidth={0}>
                            {/* Name + Chevron */}
                            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
                                <Typography noWrap sx={{ fontSize: 15, fontWeight: 700, color: C.navy, letterSpacing: -0.2 }}>
                                    {employee.nomComplet}
                                </Typography>
                                <ChevronRight size={18} color={C.gold} style={{ opacity: 0.5, flexShrink: 0 }} />
                            </Stack>

                            {/* Role badge + status */}
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" mb={1.5} alignItems="center">
                                <Chip
                                    label={role.label}
                                    size="small"
                                    sx={{
                                        bgcolor: role.bg,
                                        color: role.color,
                                        fontWeight: 600,
                                        fontSize: 11,
                                        height: 24,
                                    }}
                                />
                                <StatusChip label={status.label} color={status.color} />
                            </Stack>

                            {/* Info row */}
                            <Stack direction="row" spacing={2.5} sx={{ fontSize: 12, color: C.muted }}>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Mail size={13} /> {employee.emailPro || '—'}
                                </Box>
                            </Stack>
                            {/* Info row */}
                            <Stack direction="row" spacing={2.5} sx={{ fontSize: 12, color: C.muted }}>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Smartphone size={13} /> {employee.telephonePro || '—'}
                                </Box>
                            </Stack>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        </motion.div>
    );
}

// ── Employee Detail Drawer ────────────────────────────────────────────────────
function EmployeeDetailDrawer({ employee, open, onClose }) {
    if (!employee) return null;

    const role = roleConfig[employee.poste] || { label: employee.poste, color: C.muted };
    const contract = contractConfig[employee.typeContrat] || { label: employee.typeContrat, color: C.muted };
    const status = statusConfig[employee.actif] || { label: employee.actif, color: C.muted };
    const leavePercent = employee.totalLeaveDays > 0
        ? ((employee.totalLeaveDays - employee.remainingLeaveDays) / employee.totalLeaveDays) * 100 : 0;
    const seniority = employee?.dateEmbauche ? Math.floor((Date.now() - new Date(employee?.dateEmbauche).getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : 1;

    const barColor = leavePercent > 80 ? 'linear-gradient(135deg,#b71c1c,#c62828)'
        : leavePercent > 50 ? 'linear-gradient(135deg,#e65100,#ef6c00)'
            : `linear-gradient(135deg,${C.goldDark},${C.gold})`;

    const InfoBox = ({ label, value, color }) => (
        <Box sx={{ bgcolor: 'rgba(201,168,76,0.04)', borderRadius: 2, p: 1.5, border: `1px solid ${C.borderLight}` }}>
            <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: C.faint, mb: 0.5, fontWeight: 500 }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: color || C.navy }}>{value}</Typography>
        </Box>
    );

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 500 },
                    bgcolor: C.bg,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                }
            }}
        >
            <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: C.navy }}>
                        Fiche Employé
                    </Typography>
                    <IconButton onClick={onClose} size="small" sx={{ color: C.muted, '&:hover': { color: C.navy } }}>
                        <XCircle size={20} />
                    </IconButton>
                </Stack>

                {/* Profile Header */}
                <Stack direction="row" spacing={2.5} alignItems="center" mb={3}>
                    <Box sx={{
                        width: 72, height: 72, borderRadius: '50%',
                        background: `linear-gradient(135deg,${C.navy},${C.navyLight})`,
                        color: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, fontWeight: 700, flexShrink: 0,
                        boxShadow: '0 4px 16px rgba(26,26,46,0.25)',
                    }}>
                        {employee.prenom?.[0]}{employee.nom?.[0]}
                    </Box>
                    <Box flex={1}>
                        <Typography sx={{ fontSize: 22, fontWeight: 700, color: C.navy, mb: 0.5 }}>
                            {employee.prenom} {employee.nom}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: C.muted, mb: 1, fontFamily: 'monospace', letterSpacing: 0.5 }}>
                            {employee.employeeNumber}
                        </Typography>
                        <Stack direction="row" spacing={0.75}>
                            <StatusChip label={role.label} color={role.color} />
                            <StatusChip label={status.label} color={status.color} />
                        </Stack>
                    </Box>
                </Stack>

                <SectionDivider />

                {/* Info Grid */}
                <Grid container spacing={1.5} mb={3}>
                    <Grid item xs={6}><InfoBox label="Département" value={employee.department || '—'} /></Grid>
                    <Grid item xs={6}><InfoBox label="Contrat" value={contract.label} color={contract.color} /></Grid>
                    <Grid item xs={6}><InfoBox label="Ancienneté" value={`${seniority} an${seniority > 1 ? 's' : ''}`} /></Grid>
                    <Grid item xs={6}><InfoBox label="Salaire brut" value={`${employee?.salaire ? employee?.salaire?.toLocaleString('fr-FR') : '—'}`} /></Grid>
                </Grid>

                {/* Contact */}
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <Phone size={16} color={C.gold} />
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.navy }}>Contact</Typography>
                </Stack>
                <Paper sx={{ bgcolor: C.surface, border: `1px solid ${C.borderLight}`, borderRadius: 2, p: 2, mb: 2.5 }}>
                    <Stack spacing={1.5}>
                        {[
                            { icon: <Mail size={15} />, val: employee.emailPro || '—' },
                            { icon: <Phone size={15} />, val: employee.telephonePro || '—' },
                            { icon: <MapPin size={15} />, val: employee?.address || '—' },
                        ].map(({ icon, val }, i) => (
                            <Stack key={i} direction="row" spacing={1.5} alignItems="center" sx={{ fontSize: 13, color: '#555' }}>
                                <Box sx={{ color: C.gold }}>{icon}</Box>
                                <span>{val}</span>
                            </Stack>
                        ))}
                    </Stack>
                </Paper>

                {/* Key dates */}
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <CalendarDays size={16} color={C.gold} />
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.navy }}>Dates clés</Typography>
                </Stack>
                <Paper sx={{ bgcolor: C.surface, border: `1px solid ${C.borderLight}`, borderRadius: 2, p: 2, mb: 2.5 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: C.faint, mb: 0.5 }}>Naissance</Typography>
                            <Typography sx={{ fontSize: 13, color: '#555', fontWeight: 500 }}>
                                {employee.birthDate ? new Date(employee.birthDate).toLocaleDateString('fr-FR') : '—'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: C.faint, mb: 0.5 }}>Embauche</Typography>
                            <Typography sx={{ fontSize: 13, color: '#555', fontWeight: 500 }}>
                                {employee?.dateEmbauche ? new Date(employee?.dateEmbauche).toLocaleDateString('fr-FR') : '—'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Leave balance */}
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <CalendarDays size={16} color={C.gold} />
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.navy }}>Historique de paie</Typography>
                </Stack>
                <Paper sx={{ border: `1px solid ${C.borderLight}`, borderRadius: 2, p: 2, mb: 2.5, bgcolor: C.surface }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>

                    </Stack>
                </Paper>

                {/* Bank info */}
                {employee.bankName && (
                    <>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                            <CreditCard size={16} color={C.gold} />
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.navy }}>Informations bancaires</Typography>
                        </Stack>
                        <Paper sx={{ bgcolor: C.surface, border: `1px solid ${C.borderLight}`, borderRadius: 2, p: 2, mb: 2.5 }}>
                            <Typography sx={{ fontSize: 13, color: '#555', mb: 0.5, fontWeight: 500 }}>{employee.bankName}</Typography>
                            <Typography sx={{ fontSize: 12, color: C.muted, fontFamily: 'monospace', letterSpacing: 0.5 }}>
                                {employee.iban}
                            </Typography>
                        </Paper>
                    </>
                )}

                {/* Emergency contact */}
                {employee.emergencyContact && (
                    <>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                            <Shield size={16} color={C.gold} />
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.navy }}>Contact d'urgence</Typography>
                        </Stack>
                        <Paper sx={{ bgcolor: C.surface, border: `1px solid ${C.borderLight}`, borderRadius: 2, p: 2, mb: 2.5 }}>
                            <Typography sx={{ fontSize: 13, color: '#555' }}>
                                {employee.emergencyContact} — {employee.emergencyPhone}
                            </Typography>
                        </Paper>
                    </>
                )}

                {/* Notes */}
                {employee.notes && (
                    <>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                            <FileText size={16} color={C.gold} />
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.navy }}>Notes</Typography>
                        </Stack>
                        <Paper sx={{ border: `1px solid ${C.borderLight}`, borderRadius: 2, p: 2, bgcolor: C.surface }}>
                            <Typography sx={{ fontSize: 13, color: '#555', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                {employee.notes}
                            </Typography>
                        </Paper>
                    </>
                )}
            </Box>
        </Drawer>
    );
}

// ── Add Employee Dialog ───────────────────────────────────────────────────────
function AddEmployeeDialog({ open, onOpenChange, onSuccess }) {
    const emptyForm = {
        nom: '', prenom: '', emailPro: '', telephonePro: '', poste: '',
    };

    const [form, setForm] = useState(emptyForm);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // NOUVEAUX ÉTATS : Pour gérer l'affichage du mot de passe temporaire
    const [createdCredentials, setCreatedCredentials] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(emptyForm);
            setError('');
            setCreatedCredentials(null); // Réinitialise la vue de succès
            setCopied(false);
        }
    }, [open]);

    const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = async () => {
        if (!form.nom || !form.prenom || !form.emailPro || !form.poste) {
            setError('Veuillez remplir tous les champs obligatoires (marqués avec *)');
            return;
        }
        setIsSubmitting(true);
        setError('');

        try {
            // Utilisation de apiClient.post, Axios gère le JSON tout seul
            const response = await apiClient.post('/api/personnel/agents', form);

            // Succès : On stocke les identifiants pour afficher l'étape 2 (et on ne ferme pas le modal)
            setCreatedCredentials({
                username: response.data.username,
                motDePasseTemporaire: response.data.motDePasseTemporaire,
                nomComplet: response.data.nomComplet
            });

            // On notifie le parent que l'ajout a réussi (pour recharger le tableau en arrière-plan)
            if (onSuccess) onSuccess();

        } catch (error) {
            // Axios permet d'extraire facilement le message d'erreur renvoyé par ton backend
            const backendMessage = error.response?.data?.message || error.response?.data?.error;
            setError(backendMessage || "Erreur lors de l'ajout de l'employé. Veuillez réessayer.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCopy = () => {
        if (!createdCredentials) return;
        const textToCopy = `Bonjour ${createdCredentials.nomComplet},\n\nVoici vos identifiants d'accès :\nNom d'utilisateur : ${createdCredentials.username}\nMot de passe temporaire : ${createdCredentials.motDePasseTemporaire}\n\nIl vous sera demandé de modifier ce mot de passe à votre première connexion.`;

        navigator.clipboard.writeText(textToCopy).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 3000); // Reset du feedback visuel après 3s
        });
    };

    // Style de tes champs (basé sur ton code)
    const fieldSx = { ...inputSx, mb: 0 };

    return (
        <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="sm" fullWidth
            PaperProps={{ sx: { bgcolor: C.bg, borderRadius: 3 } }}>

            {createdCredentials ? (
                /* ==========================================
                   VUE 2 : SUCCÈS & IDENTIFIANTS
                   ========================================== */
                <>
                    <DialogTitle sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        color: '#2e7d32', fontSize: 18, fontWeight: 700, pb: 1.5
                    }}>
                        <Box sx={{
                            width: 36, height: 36, borderRadius: 2, bgcolor: 'rgba(46,125,50,0.1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2e7d32',
                        }}>
                            <CheckCircle2 size={18} />
                        </Box>
                        Agent créé avec succès
                    </DialogTitle>

                    <DialogContent dividers sx={{ borderColor: C.borderLight }}>
                        <Box sx={{ pt: 1, pb: 1 }}>
                            <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 13, mb: 3 }}>
                                Veuillez copier ces informations et les transmettre à l'agent.
                                <strong> Le mot de passe temporaire ne sera plus jamais affiché.</strong>
                            </Alert>

                            <Box sx={{
                                p: 2.5,
                                bgcolor: 'rgba(201,168,76,0.05)',
                                borderRadius: 2,
                                border: `1px solid ${C.borderLight}`,
                                position: 'relative'
                            }}>
                                <Typography variant="body2" sx={{ color: C.navy, mb: 1.5, fontWeight: 600 }}>
                                    Identifiants de connexion :
                                </Typography>

                                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 1, color: C.navy }}>
                                    <strong style={{ opacity: 0.7 }}>Utilisateur :</strong> {createdCredentials.username}
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: 'monospace', color: C.navy }}>
                                    <strong style={{ opacity: 0.7 }}>Mot de passe :</strong> {createdCredentials.motDePasseTemporaire}
                                </Typography>

                                <Tooltip title={copied ? "Copié !" : "Copier les accès"}>
                                    <IconButton
                                        onClick={handleCopy}
                                        sx={{
                                            position: 'absolute', top: 12, right: 12,
                                            color: copied ? '#2e7d32' : C.gold
                                        }}
                                    >
                                        <Copy size={20} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2.5 }}>
                        <Button onClick={() => onOpenChange(false)} sx={{ ...goldBtnSx, minWidth: 140 }}>
                            Terminer et fermer
                        </Button>
                    </DialogActions>
                </>
            ) : (
                /* ==========================================
                   VUE 1 : FORMULAIRE DE CRÉATION
                   ========================================== */
                <>
                    <DialogTitle sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        color: C.navy, fontSize: 18, fontWeight: 700, pb: 1.5
                    }}>
                        <Box sx={{
                            width: 36, height: 36, borderRadius: 2, bgcolor: 'rgba(201,168,76,0.1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gold,
                        }}>
                            <UserPlus size={18} />
                        </Box>
                        Nouvel Employé
                    </DialogTitle>

                    <DialogContent dividers sx={{ borderColor: C.borderLight }}>
                        <Box sx={{ pt: 1, pb: 1 }}>
                            {error && (
                                <Alert severity="error" sx={{ borderRadius: 2, fontSize: 13, mb: 3 }}>
                                    {error}
                                </Alert>
                            )}

                            <Grid container spacing={3}>
                                {/* Prénoms */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: C.navy }}>
                                            Prénoms <span style={{ color: '#d32f2f' }}>*</span>
                                        </FormLabel>
                                        <OutlinedInput
                                            value={form.prenom}
                                            onChange={update('prenom')}
                                            sx={fieldSx}
                                            required
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Nom */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: C.navy }}>
                                            Nom <span style={{ color: '#d32f2f' }}>*</span>
                                        </FormLabel>
                                        <OutlinedInput
                                            value={form.nom}
                                            onChange={update('nom')}
                                            sx={fieldSx}
                                            required
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Email */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: C.navy }}>
                                            Email professionnel <span style={{ color: '#d32f2f' }}>*</span>
                                        </FormLabel>
                                        <OutlinedInput
                                            type="email"
                                            value={form.emailPro}
                                            onChange={update('emailPro')}
                                            sx={fieldSx}
                                            required
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Téléphone */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: C.navy }}>
                                            Téléphone
                                        </FormLabel>
                                        <OutlinedInput
                                            value={form.telephonePro}
                                            onChange={update('telephonePro')}
                                            sx={fieldSx}
                                            inputProps={{
                                                inputMode: 'numeric'
                                            }}
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Poste */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: C.navy }}>
                                            Poste <span style={{ color: '#d32f2f' }}>*</span>
                                        </FormLabel>
                                        <Select
                                            value={form.poste}
                                            onChange={update('poste')}
                                            displayEmpty
                                            sx={fieldSx}
                                            required
                                        >
                                            <MenuItem value="" disabled>
                                                <em style={{ color: '#aaa', fontStyle: 'normal' }}>Sélectionner un poste</em>
                                            </MenuItem>
                                            {Object.entries(roleConfig).map(([key, cfg]) => (
                                                <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                        <Button onClick={() => onOpenChange(false)} variant="outlined"
                            sx={{ borderColor: 'rgba(201,168,76,0.25)', color: C.navy, textTransform: 'none', borderRadius: 2 }}>
                            Annuler
                        </Button>
                        <Button onClick={handleSubmit} disabled={isSubmitting} sx={{ ...goldBtnSx, minWidth: 140 }}
                            startIcon={isSubmitting ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <UserPlus size={16} />}>
                            {isSubmitting ? 'Ajout...' : "Ajouter l'employé"}
                        </Button>
                    </DialogActions>
                </>
            )}

            {/* Notification de copie */}
            <Snackbar
                open={copied}
                message="Identifiants copiés dans le presse-papiers !"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                ContentProps={{ sx: { bgcolor: '#2e7d32', color: 'white', fontWeight: 600, borderRadius: 2 } }}
            />
        </Dialog>
    );
}

// ── Animated Table Row ───────────────────────────────────────────────────────
function AnimatedTableRow({ children, index }) {
    return (
        <TableRow
            component={motion.tr}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.02, duration: 0.25 }}
            sx={{
                '&:hover': { bgcolor: 'rgba(201,168,76,0.02)' },
                transition: 'background 0.15s',
            }}
        >
            {children}
        </TableRow>
    );
}

// ── Main Personnel Module ─────────────────────────────────────────────────────
export function PersonnelModule() {
    const [employees, setEmployees] = useState([]);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [salaryRecords, setSalaryRecords] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(false);
    const [activeSection, setActiveSection] = useState('employees');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('ALL');
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [addEmployeeOpen, setAddEmployeeOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchData = useCallback(async () => {
        setLoading(true);
        setApiError(false);
        try {
            const [empRes, statsRes] = await Promise.all([
                apiClient.get('/api/personnel/agents'),
                apiClient.get('/api/reports/agents/stats'),
            ]);

            setEmployees(Array.isArray(empRes.data) ? empRes.data : []);
            setStats(statsRes.data || {});
        } catch (error) {
            console.error("Erreur de récupération des données:", error);
            setApiError(true);
            setStats({});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSelectEmployee = (emp) => { setSelectedEmployee(emp); setDetailOpen(true); };

    const handleApproveLeave = async (leaveId) => {
        try {
            await apiClient.post('/api/personnel/agents', {
                action: 'approve_leave',
                leaveId,
                approvedBy: 'Admin'
            });
            fetchData();
        } catch { /* silencieux */ }
    };

    const handleRejectLeave = async (leaveId) => {
        try {
            await apiClient.post('/api/personnel/agents', {
                action: 'reject_leave',
                leaveId,
                rejectedBy: 'Admin'
            });
            fetchData();
        } catch { /* silencieux */ }
    };

    const handleProcessSalary = async (salaryId) => {
        try {
            await apiClient.post('/api/personnel', {
                action: 'process_salary',
                salaryId
            });
            fetchData();
        } catch { /* silencieux */ }
    };

    const filteredEmployees = employees.filter((emp) => {
        const matchSearch = !searchTerm ||
            `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.employeeNumber?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchRole = filterRole === 'ALL' || emp.role === filterRole;
        const matchStatus = filterStatus === 'ALL' || emp.status === filterStatus;
        return matchSearch && matchRole && matchStatus;
    });

    // ── Loading state ──
    if (loading) {
        return <SkeletonLoader />;
    }

    // ── Error state ──
    if (apiError) {
        return <ErrorState onRetry={fetchData} />;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ── KPI Cards ── */}
            <Grid container spacing={2}>
                <Grid item xs={6} md={4} lg={3} mb={3}><KpiCard title="Total" value={stats?.total ?? 0} subtitle="Employés enregistrés" icon={<Users />} color="#28026eff" bgColor="rgba(40, 2, 110,0.1)" delay={0} /></Grid>
                <Grid item xs={6} md={4} lg={3} mb={3}><KpiCard title="Actif" value={stats?.actif ?? 0} subtitle="Employés actifs" icon={<UserCheck />} color="#197602ff" bgColor="rgba(25, 118, 2,0.1)" delay={0} /></Grid>
                <Grid item xs={6} md={4} lg={3} mb={3}><KpiCard title="Inactif" value={stats?.inactif ?? 0} subtitle="Employés inactifs" icon={<UserMinus />} color="#dd3b22ff" bgColor="rgba(221, 59, 34,0.1)" delay={0} /></Grid>
            </Grid>

            {/* ── Section Tabs (MUI Tabs natif) ── */}
            <Card sx={{ ...cardSx, p: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1 }}>
                    <Tabs
                        value={activeSection}
                        onChange={(_, v) => setActiveSection(v)}
                        textColor="inherit"
                        TabIndicatorProps={{ style: { background: C.gold, height: 3, borderRadius: '3px 3px 0 0' } }}
                        sx={{
                            minHeight: 48,
                            '& .MuiTabs-flexContainer': { gap: 0.5 },
                        }}
                    >
                        {sections.map((section) => (
                            <Tab
                                key={section.id}
                                value={section.id}
                                label={
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        {section.icon}
                                        <span>{section.label}</span>
                                    </Stack>
                                }
                                sx={{
                                    textTransform: 'none',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    minHeight: 44,
                                    px: 2.5,
                                    color: C.muted,
                                    borderRadius: 2,
                                    '&.Mui-selected': { color: C.gold, bgcolor: 'rgba(201,168,76,0.08)' },
                                    '&:hover': { color: C.gold, bgcolor: 'rgba(201,168,76,0.04)' },
                                }}
                            />
                        ))}
                    </Tabs>
                    <Box px={1}>
                        <Button
                            onClick={() => setAddEmployeeOpen(true)}
                            sx={{ ...goldBtnSx, height: 36, px: 2 }}
                            startIcon={<UserPlus size={16} />}
                        >
                            {!isMobile && 'Ajouter'}
                        </Button>
                    </Box>
                </Box>
            </Card>

            {/* ── Sections ── */}
            <AnimatePresence mode="wait">
                {/* EMPLOYEES */}
                {activeSection === 'employees' && (
                    <motion.div key="employees" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} style={{ mt: 2 }}>
                        <Stack spacing={2.5}>
                            {/* Filters */}
                            <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 1.5, alignItems: "center", justifyContent: "space-between" }}>
                                <TextField
                                    placeholder="Rechercher un employé..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    size="small"
                                    sx={{ ...inputSx, flex: 1, minWidth: 200, maxWidth: 400 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search size={16} color={C.faint} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <FormControl size="small" sx={{ ...inputSx, minWidth: 160 }}>
                                    <InputLabel>Poste</InputLabel>
                                    <Select value={filterRole} label="Poste" onChange={(e) => setFilterRole(e.target.value)}>
                                        <MenuItem value="ALL">Tous les postes</MenuItem>
                                        {Object.entries(roleConfig).map(([key, cfg]) => (
                                            <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ ...inputSx, minWidth: 144 }}>
                                    <InputLabel>Statut</InputLabel>
                                    <Select value={filterStatus} label="Statut" onChange={(e) => setFilterStatus(e.target.value)}>
                                        <MenuItem value="ALL">Tous</MenuItem>
                                        {Object.entries(statusConfig).map(([key, cfg]) => (
                                            <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Grid */}
                            <Grid container spacing={2}>
                                {filteredEmployees.map((emp) => (
                                    <Grid item xs={12} md={6} xl={4} key={emp.id}>
                                        <EmployeeCard employee={emp} onSelect={handleSelectEmployee} />
                                    </Grid>
                                ))}
                                {filteredEmployees.length === 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                        <EmptyState
                                            icon={<Users size={40} color={C.dimmer} style={{ opacity: 0.4 }} />}
                                            title="Aucun employé trouvé"
                                            subtitle="Essayez d'ajuster vos filtres de recherche"
                                        />
                                    </Box>
                                )}
                            </Grid>
                        </Stack>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Employee Detail Drawer */}
            <EmployeeDetailDrawer
                employee={selectedEmployee}
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
            />

            {/* Add Employee Dialog */}
            <AddEmployeeDialog
                open={addEmployeeOpen}
                onOpenChange={setAddEmployeeOpen}
                onSuccess={fetchData}
            />
        </Box>
    );
}

export default PersonnelModule;
